import _ from 'lodash';
import { Miscellaneous } from '@/constants/Miscellaneous';
import IMlsImportResult from '@/models/mls/IMlsImportResult';
import MlsPropertyItemUtils from '@/models/mls/MlsPropertyItemUtils';
import IMLSData, { newMlsData } from '@/models/mls/IMLSData';
import IMLSPropertyItem from '@/models/mls/IMLSPropertyItem';
import IMLSRawImportMlsInfo from '@/models/mls/IMLSRawImportMlsInfo';
import IMLSRawImport from '@/models/mls/IMLSRawImport';
import { Actions, Getters, mlsStore, mlsStoreV36, store } from '@/store';
import GuideManager from '../GuideManager';
import { FieldNames } from '@/constants/FieldNames';
import LogUsageManager from '../LogUsageManager';
import IMlsImportStats, { newMlsImportStats } from '@/models/mls/IMlsImportStats';
import { MlsMapperEventKeys } from '@/constants/MlsMapperEventKeys';
import IComp from '@/models/Comp';
import { newComp } from '@/models/Comp';
import ICompAddresses from '@/models/comp/ICompAddresses';
import { newMlsMapperMessage } from '@/models/mls/IMlsMapperMessage';
import IMlsPreviouslyViewed from '@/models/mls/IMlsPreviouslyViewed';
import CompsUtils from '@/models/comp/CompsUtils';
import { newProcessCompRequest } from '@/store/3.6/models/IProcessCompRequest';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import HostExchangeService from '@/services/3.6/HostExchangeService';
import IMlsImportResult36, { IMlsImportFieldData36, IMlsImportProperty36 } from '@/models/mls/IMlsImportResult36';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import IFeedData from '@/models/comp/IFeedData';
import MLSService from '@/services/3.6/MLSService';
import IFeedDatas from '@/models/comp/IFeedDatas';
import { newCompAddress } from '@/models/CompAddress';
import ICompAddress from '@/models/CompAddress';
import CompsManager from './CompsManager';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IAddress1UnitNoZipCode from '@/models/common/IAddress1UnitNoZipCode';
import Address1UnitNoZipCodeUtils from '@/models/common/Address1UnitNoZipCodeUtils';

/**
 * MLS Import management.
 */
export default {
    /**
     * After launch MLS Mapper sends back a 'loaded' message indicating it's ready to receive additional values.
     * Note: The MLS mapper query parameters already includes the source, MLS token, p2id, usage Guid and form code.
     * @param contentWindow
     */
    postMessagesToIFrame(contentWindow: Window | null) {
        if (contentWindow) {
            postAddressesMessage(contentWindow);
            postImportLimitMessage(contentWindow);
        }
    },

    convertToSimplePayload(rawImport: IMLSRawImport): IMlsImportResult36 {
        const result: IMlsImportProperty36[] = [];

        const subjectAddress = getSubjectAddress();
        let subjectMatched = false;

        if (rawImport?.propertyData?.length > 0) {
            for (const propertyItem of rawImport.propertyData) {
                const fieldData: IMlsImportFieldData36[] = [];

                for (const field of propertyItem.fields) {
                    if (field.formFieldName !== '') {
                        fieldData.push({
                            fieldName: field.formFieldName,
                            value: getFieldValue(field),
                        });
                    }
                }

                // Once the Subject's address has been matched to an MLS property, stop checking the rest -
                // only one property can be the Subject.
                let isSubject = false;
                if (!subjectMatched && subjectAddress) {
                    const propertyAddress = MlsPropertyItemUtils.getAddress(propertyItem);
                    if (addressesMatchExact(propertyAddress, subjectAddress)) {
                        isSubject = true;
                        subjectMatched = true;
                    }
                }

                result.push({
                    IsSubject: isSubject,
                    FieldData: fieldData,
                });
            }
        }

        // Convert the raw MLS data to the below shape.  We will then pass this
        // off to a QS endpoint to convert it into the standard 3.6 shape.
        // 
        // [
        //     {
        //     "IsSubject": false,
        //         "FieldData": [
        //                 { "fieldName": "AD_ADDR1", "value": "539 Shirley LN NE" },
        //                 { "fieldName": "DB_CITY", "value": "Piedmont" },
        //                 { "fieldName": "DB_STATE", "value": "OK" }
        //         ]
        //     },
        //     {
        //     "IsSubject": false,
        //         "FieldData": [
        //                 { "fieldName": "AD_ADDR1", "value": "600 Shirley LN NE" },
        //                 { "fieldName": "DB_CITY", "value": "Piedmont" },
        //                 { "fieldName": "DB_STATE", "value": "OK" }
        //         ]
        //     },
        //     {
        //     "IsSubject": false,
        //         "FieldData": [
        //             { "fieldName": "AD_ADDR1", "value": "605 Shirley LN NE" },
        //             { "fieldName": "DB_CITY", "value": "Piedmont" },
        //             { "fieldName": "DB_STATE", "value": "OK" }
        //         ]
        //     }
        // ]

        return result as IMlsImportResult36;
    },

    /**
     * Fetch the raw MLS data from the ConvertMLSMapperDataToUAD36 endpoint (one flat field-name/value map per
     * property) and transform each into the common/UAD 3.6 IFeedData shape (address + flattened sourceData),
     * same as PublicRecords/SmartExchange/Database do for their own raw provider payloads.
     * @param simpleCSVPayload
     * @returns
     */
    async fetchMLSCommonShape(simpleCSVPayload: IMlsImportResult36): Promise<IFeedDatas | null> {
        const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
        const service = new MLSService(webEnvConfig);

        const datas = await service.getMLSData(simpleCSVPayload);
        if (!datas || datas.length === 0) {
            return null;
        }

        const feedDatas: IFeedDatas = [];
        for (const data of datas) {
            const processedData = await CompsManager.transformSourceDataPayload(data, SourceDataInitials.Mls);
            if (processedData) {
                feedDatas.push(processedData);
            }
        }

        return feedDatas.length > 0 ? feedDatas : null;
    },

    /**
     * Take the payload received from MLS Mapper and return an MlsImportResult type from it.
     * @param payload
     * @returns
     */
    getImportResultFromPayload(payload: string): IMlsImportResult {
        const importResult = payload as unknown as IMlsImportResult;
        return importResult;
    },

    /**
     * Saves the given import result to the store, including the MLS properties and the
     * CSV file info.
     * Stops adding when the import limit is reached but continues to update existing
     * MLS properties.
     * Saves the import stats to the store, containing various counts and address data on those added or replaced.
     * Uses the CompsManager to process the added & replaced MLS properties.
     * @param importResult
     * @returns the import stats containing various counts and address data on those added or replaced.
     */
    async processAndStore(importResult: IMlsImportResult): Promise<IMlsImportStats> {
        mapHeaderData(importResult.data);

        const importStats = newMlsImportStats();

        if (importResult.data.propertyData.length > 0) {
            importStats.count = importResult.data.propertyData.length;

            for (const propertyItem of importResult.data.propertyData) {
                const address1UnitNoZipCode = MlsPropertyItemUtils.getAddress1UnitNoAndZipCode(propertyItem);
                if (address1UnitNoZipCode.address1 && address1UnitNoZipCode.zipCode) {
                    const storePropertyItem = mlsStore.MlsPropertyByAddress1UnitNoZipCode(address1UnitNoZipCode);
                    if (storePropertyItem) {
                        fixupValues(propertyItem);
                        addMlsName(propertyItem, importResult.data.mlsInfo);

                        // This MLS property exists. Replace it. Does not count toward import limit.
                        await mlsStore.addOrReplaceProperty(propertyItem);
                        importStats.replaced++;
                        importStats.included.push(address1UnitNoZipCode);
                    } else {
                        // This MLS property does not exist. Check import limit before adding.
                        if (importStats.added < Miscellaneous.ImportLimit) {
                            fixupValues(propertyItem);
                            addMlsName(propertyItem, importResult.data.mlsInfo);

                            const added = await mlsStore.addOrReplaceProperty(propertyItem);
                            if (added) {
                                importStats.added++;
                                importStats.included.push(address1UnitNoZipCode);
                            }
                        } else {
                            importStats.ignored++;
                        }
                    }
                } else {
                    importStats.errors++;

                    const address = MlsPropertyItemUtils.getAddress(propertyItem);
                    LogUsageManager.importMlsDataError(address);
                }
            }

            if (importStats.added > 0 || importStats.replaced > 0) {
                const csvFileInfo = importResult.data.csvData ?? null;
                if (csvFileInfo) {
                    await mlsStore.addCsvFileInfo(csvFileInfo);
                }

                LogUsageManager.importedMlsData(importStats.added);

                GuideManager.guideCompleted(3);
            }
        }

        mlsStore.setImportStats(importStats);

        return importStats;
    },

    /**
     * Saves the given MLS feed data (new common/UAD shape) to the mlsStoreV36 feed-data store, by address,
     * mirroring processAndStore()'s dedup/import-limit/stats bookkeeping but for IFeedData[] instead of
     * IMLSPropertyItem[]. Also captures each property's mediaUrls (additional photos) from the raw MLS Mapper
     * payload into mlsStoreV36, since the new backend endpoint's flat field maps don't carry mediaUrls at all.
     * @param feedDatas
     * @param mlsInfo used to set the MLS column header name (FieldNames.MlsImportName) via addMlsName36
     * @param rawImport the raw MLS Mapper payload (event.data.data) - used only to source mediaUrls by address
     * @returns the import stats containing various counts and address data on those added or replaced.
     */
    async processAndStore2(feedDatas: IFeedDatas, mlsInfo: IMLSRawImportMlsInfo, rawImport?: IMLSRawImport): Promise<IMlsImportStats> {
        const importStats = newMlsImportStats();

        if (feedDatas && feedDatas.length > 0) {
            importStats.count = feedDatas.length;

            for (const feedData of feedDatas) {
                const address1UnitNoZipCode = MlsPropertyItemUtils.getAddress1UnitNoAndZipCode36(feedData);
                if (address1UnitNoZipCode.address1 && address1UnitNoZipCode.zipCode) {
                    addMlsName36(feedData, mlsInfo);
                    await collectMediaUrls36(rawImport, address1UnitNoZipCode, feedData);

                    const existingFeedData = mlsStoreV36.MlsFeedDataByAddress1UnitNoZipCode(address1UnitNoZipCode);
                    if (existingFeedData) {
                        // This MLS property exists. Replace it. Does not count toward import limit.
                        await mlsStoreV36.addOrReplaceFeedData(feedData);
                        importStats.replaced++;
                        importStats.included.push(address1UnitNoZipCode);
                    } else {
                        // This MLS property does not exist. Check import limit before adding.
                        if (importStats.added < Miscellaneous.ImportLimit) {
                            const added = await mlsStoreV36.addOrReplaceFeedData(feedData);
                            if (added) {
                                importStats.added++;
                                importStats.included.push(address1UnitNoZipCode);
                            }
                        } else {
                            importStats.ignored++;
                        }
                    }
                } else {
                    importStats.errors++;
                    LogUsageManager.importMlsDataError(feedData.address ?? newCompAddress());
                }
            }

            if (importStats.added > 0 || importStats.replaced > 0) {
                // The new pipeline's backend response has no csvData - only the raw MLS Mapper payload does.
                const csvFileInfo = rawImport?.csvData ?? null;
                if (csvFileInfo) {
                    await mlsStoreV36.addCsvFileInfo(csvFileInfo);
                }

                LogUsageManager.importedMlsData(importStats.added);
            }
        }

        mlsStoreV36.setImportStats(importStats);

        return importStats;
    },

    /**
     * Get an array of previously viewed for the address data in import stats
     * @param importResult
     * @returns
     */
    getExistingPreviouslyVieweds(): IMlsPreviouslyViewed[] {
        const previouslyVieweds: IMlsPreviouslyViewed[] = [];

        const importStats = mlsStoreV36.MlsImportStats;
        if (importStats.included.length > 0) {
            const comps: IComp[] = store.getters[Getters.CompAddressesV36.Comps];

            for (const address1UnitNoZipCode of importStats.included) {
                const comp = CompsUtils.getByAddress1UnitNoAndZipCode(comps, address1UnitNoZipCode);
                if (comp && comp.hasVisited) {
                    const previouslyViewed: IMlsPreviouslyViewed = {
                        compNumber: comp.compNumber,
                        address1UnitNoZipCode: address1UnitNoZipCode,
                    };
                    previouslyVieweds.push(previouslyViewed);
                }
            }
        }

        return previouslyVieweds;
    },

    /**
     * Process the imported properties as comps, adding or replacing as needed.
     * Reads addresses from mlsStoreV36.MlsImportStats (set by processAndStore2) and the matching feed data from
     * mlsStoreV36.MlsFeedDataByAddress1UnitNoZipCode - the already-built {address, sourceData} shape, so no
     * MlsDataConverter step is needed here (unlike the old IMLSPropertyData pipeline).
     *
     * @param runPreferencesOnViewed A flag indicating whether existing viewed comps should have preferences run
     */
    async processToComps(runPreferencesOnViewed: boolean): Promise<void> {
        const importStats = mlsStoreV36.MlsImportStats;

        const mlsComps: IComp[] = [];
        for (const address1UnitNoZipCode of importStats.included) {
            const feedData = mlsStoreV36.MlsFeedDataByAddress1UnitNoZipCode(address1UnitNoZipCode);
            if (feedData) {
                // Clone - feedData is a live reference into mlsStoreV36 state; ProcessComp mutates comp.address/sourceData directly.
                const clonedFeedData: IFeedData = _.cloneDeep(feedData);
                const comp: IComp = newComp();
                comp.address = clonedFeedData.address ?? newCompAddress();
                // CompIdxByAddress dedup does strict '===' on unitNumber - transformSourceDataPayload never sets it,
                // leaving it undefined, which never matches an existing comp's '' and causes duplicate comp tabs.
                comp.address.unitNumber = comp.address.unitNumber ?? '';
                comp.sourceDataMLS = clonedFeedData;
                mlsComps.push(comp);
            }
        }

        if (mlsComps.length > 0) {
            const almCompId = theLocalStorageUtils.get(LocalStorageKeys.almCompId) ?? '';
            const hostExchangeService = HostExchangeService.getInstance();

            for (const mlsComp of mlsComps) {
                const compIdx: number = store.getters[Getters.CompAddressesV36.CompIdxByAddress](mlsComp.address);
                if (compIdx === -1) {
                    // Send the new comp address to our host for adding before processing
                    // Comp number is important here, give it the next in sequence
                    const compCount: number = store.getters[Getters.CompAddressesV36.CompCount];
                    mlsComp.compNumber = compCount;
                    await hostExchangeService.addCompAddress(almCompId, mlsComp);
                }

                const processCompRequest = newProcessCompRequest(mlsComp);
                processCompRequest.onUpdateRunPreferencesWhenHasVisited = runPreferencesOnViewed;

                store.dispatch(Actions.CompAddressesV36.ProcessComp, processCompRequest); // not awaiting
            }
        }
    },

    relaunchJumpStartModal(): boolean {
        const comps = store.getters[Getters.CompAddressesV36.Comps];
        if (comps.length === 0 || comps.length === 1) {
            return true;
        }
        return false;
    },
};

/**
 * Updates the given raw import, assuring each property source and related field has the headers
 * property value set if header index resolved to a value in the array of headers.
 * @param rawImport
 * @returns
 */
function mapHeaderData(rawImport: IMLSRawImport) {
    if (rawImport.propertyData.length >= 0) {
        for (const propertyItem of rawImport.propertyData) {
            for (const field of propertyItem.fields) {
                if (field.propertySource) {
                    for (const propertySource of field.propertySource) {
                        if (propertySource.headerIdx in rawImport.headers) {
                            const header = rawImport.headers[propertySource.headerIdx];
                            propertySource.headers = header;
                        }
                    }
                }

                if (field.relatedFields) {
                    for (const relatedField of field.relatedFields) {
                        if (relatedField.headerIdx in rawImport.headers) {
                            const header = rawImport.headers[relatedField.headerIdx];
                            relatedField.headers = header;
                        }
                    }
                }
            }
        }
    }
}

/**
 * Correct values in the given property item QS doesn't handle, like any NaNs.
 * @param propertyItem
 */
function fixupValues(propertyItem: IMLSPropertyItem) {
    for (const mlsData of propertyItem.fields) {
        if (typeof mlsData.formData === 'number' && isNaN(mlsData.formData)) {
            mlsData.formData = '';
        }
    }
}

/**
 * Add the MLS name to the given property item when it doesn't currently contain FieldNames.MlsImportName field.
 * @param propertyItem
 * @param mlsInfo
 */
function addMlsName(propertyItem: IMLSPropertyItem, mlsInfo: IMLSRawImportMlsInfo) {
    if (propertyItem && mlsInfo) {
        const fieldIdx = propertyItem.fields.findIndex((f) => f.formFieldName === FieldNames.MlsImportName);
        if (fieldIdx < 0) {
            const mlsData: IMLSData = newMlsData();
            mlsData.formFieldName = FieldNames.MlsImportName;
            mlsData.formData = mlsInfo.mlsName;

            propertyItem.fields.push(mlsData);
        }
    }
}

/**
 * Resolve a field's value with fallback priority: formData, then propertySource (val's joined with a space),
 * then relatedFields (val's joined with a space), then empty string if none have a value.
 * @param field
 */
function getFieldValue(field: IMLSData): string {
    if (field.formData !== '' && field.formData !== undefined && field.formData !== null) {

        const formDataValue = `${field.formData}`.replace(/undefined/g, '').trim();
        if (formDataValue !== '') {
            return formDataValue;
        }
    }

    if (field.propertySource && field.propertySource.length > 0) {
        const value = field.propertySource.map((source) => source.val).filter((val) => !!val).join(' ');
        if (value !== '') {
            return value;
        }
    }

    if (field.relatedFields && field.relatedFields.length > 0) {
        const value = field.relatedFields.map((related) => related.val).filter((val) => !!val).join(' ');
        if (value !== '') {
            return value;
        }
    }

    return '';
}

/**
 * Get the Subject comp's address, if a Subject exists in the current comps.
 * @returns
 */
function getSubjectAddress(): ICompAddress | null {
    const comps: IComp[] = store.getters[Getters.CompAddressesV36.Comps];
    const subjectComp = comps.find((c: any) => String(c.compType ?? '').toLowerCase() === 'subject');
    return subjectComp ? subjectComp.address : null;
}

/**
 * Compare address1, unitNumber, city, state and zipCode for an exact (trim/case-insensitive) match.
 * @param a
 * @param b
 */
function addressesMatchExact(a: ICompAddress, b: ICompAddress): boolean {
    const normalize = (value: string | undefined): string => (value ?? '').trim().toLowerCase();

    return normalize(a.address1) === normalize(b.address1)
        && normalize(a.unitNumber) === normalize(b.unitNumber)
        && normalize(a.city) === normalize(b.city)
        && normalize(a.state) === normalize(b.state)
        && normalize(a.zipCode) === normalize(b.zipCode);
}

/**
 * Add the MLS name to the given feed data (new common shape) when it doesn't currently contain
 * FieldNames.MlsImportName in sourceData.
 * @param feedData
 * @param mlsInfo
 */
function addMlsName36(feedData: IFeedData, mlsInfo: IMLSRawImportMlsInfo) {
    if (feedData && mlsInfo) {
        if (!feedData.sourceData) {
            feedData.sourceData = {};
        }
        if (!feedData.sourceData[FieldNames.MlsImportName]) {
            feedData.sourceData[FieldNames.MlsImportName] = mlsInfo.mlsName;
        }
    }
}

/**
 * Find the raw MLS Mapper propertyItem matching the given address and store its mediaUrls (additional photos)
 * into mlsStoreV36, so CompsManager.getActiveCompPhotos() can fall back to them for the new pipeline (the
 * ConvertMLSMapperDataToUAD36 endpoint's flat field maps don't carry mediaUrls - only the raw client-side
 * payload does). Also sets the property's first photo as feedData.sourceData[COMP_PHOTO], so the MLS column's
 * Property Photo row has a thumbnail instead of the empty placeholder.
 * @param rawImport
 * @param address1UnitNoZipCode
 * @param feedData the already-built feed data for this address, whose sourceData gets COMP_PHOTO set
 */
async function collectMediaUrls36(rawImport: IMLSRawImport | undefined, address1UnitNoZipCode: IAddress1UnitNoZipCode, feedData: IFeedData): Promise<void> {
    if (!rawImport?.propertyData?.length) {
        return;
    }

    const propertyItem = rawImport.propertyData.find((item) =>
        Address1UnitNoZipCodeUtils.matches(MlsPropertyItemUtils.getAddress1UnitNoAndZipCode(item), address1UnitNoZipCode)
    );
    if (propertyItem?.mediaUrls && propertyItem.mediaUrls.length > 0) {
        await mlsStoreV36.addOrReplaceMediaUrls({
            address1UnitNoZipCode: address1UnitNoZipCode,
            mediaUrls: propertyItem.mediaUrls,
        });

        if (!feedData.sourceData) {
            feedData.sourceData = {};
        }
        if (!feedData.sourceData[FieldNames.CompPhoto]) {
            feedData.sourceData[FieldNames.CompPhoto] = propertyItem.mediaUrls[0].full;
        }
    }
}

/**
 * Post the addresses message to MLS Mapper
 * @param contentWindow
 */
function postAddressesMessage(contentWindow: Window) {
    const compAddresses: ICompAddresses = [];
    const comps: IComp[] = store.getters[Getters.CompAddressesV36.Comps];
    for (const comp of comps) {
        const compAddress = _.clone(comp.address);
        compAddresses.push(compAddress);
    }

    const message = newMlsMapperMessage();
    message.key = MlsMapperEventKeys.addresses;
    message.data = JSON.stringify(compAddresses);

    const messageString = JSON.stringify(message);
    contentWindow.postMessage(messageString, '*');
}

/**
 * Post the import limit message to MLS Mapper
 * @param contentWindow
 */
function postImportLimitMessage(contentWindow: Window) {
    const message = newMlsMapperMessage();
    message.key = MlsMapperEventKeys.importLimit;
    message.data = `${Miscellaneous.ImportLimit}`;

    const messageString = JSON.stringify(message);
    contentWindow.postMessage(messageString, '*');
}
